import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select } from "../ui/select";
import { Calendar, Clock, MapPin, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import moment from "moment-timezone";

export function CallbackScheduleDialog({ open, onClose, lead, onSuccess, existingCallback, mode = "schedule" }) {
    const [formData, setFormData] = useState({
        date: "",
        time: "",
        note: ""
    });
    const [timeUntil, setTimeUntil] = useState("");

    // Initialize form data
    useEffect(() => {
        if (open) {
            if (existingCallback && mode === "reschedule") {
                // Load existing callback data
                const scheduledDate = moment(existingCallback.scheduledFor);
                setFormData({
                    date: scheduledDate.format("YYYY-MM-DD"),
                    time: scheduledDate.format("HH:mm"),
                    note: existingCallback.note || ""
                });
            } else {
                // New callback - set defaults
                const now = moment();
                setFormData({
                    date: now.format("YYYY-MM-DD"),
                    time: now.add(1, 'hour').format("HH:mm"),
                    note: ""
                });
            }
        }
    }, [open, existingCallback, mode]);

    // Update countdown
    useEffect(() => {
        if (formData.date && formData.time) {
            updateTimeInfo();
            const interval = setInterval(updateTimeInfo, 60000); // Update every minute
            return () => clearInterval(interval);
        }
    }, [formData.date, formData.time]);

    const updateTimeInfo = () => {
        try {
            const scheduledDateTime = moment(`${formData.date} ${formData.time}`);
            
            // Time until callback
            const now = moment();
            const duration = moment.duration(scheduledDateTime.diff(now));
            
            if (duration.asSeconds() < 0) {
                setTimeUntil("In the past");
            } else {
                const days = Math.floor(duration.asDays());
                const hours = duration.hours();
                const minutes = duration.minutes();
                
                let timeStr = "";
                if (days > 0) timeStr += `${days}d `;
                if (hours > 0) timeStr += `${hours}h `;
                if (minutes > 0) timeStr += `${minutes}m`;
                
                setTimeUntil(timeStr.trim() || "Now");
            }
        } catch (error) {
            setTimeUntil("");
        }
    };

    const handleSubmit = () => {
        if (!formData.date || !formData.time) {
            toast.error("Please select date and time");
            return;
        }

        // Create ISO datetime in local time
        const scheduledDateTime = moment(`${formData.date} ${formData.time}`);
        
        // Check if in the past
        if (scheduledDateTime.isBefore(moment())) {
            toast.error("Cannot schedule callback in the past");
            return;
        }

        const callbackData = {
            scheduledFor: scheduledDateTime.toISOString(),
            timezone: moment.tz.guess(), // User's local timezone
            note: formData.note
        };

        onSuccess(callbackData);
    };

    const dialogTitle = mode === "reschedule" ? "Reschedule Callback" : "Schedule Callback";
    const dialogDescription = mode === "reschedule" 
        ? `Reschedule callback for ${lead?.firstName} ${lead?.lastName}`
        : `Set a callback time for ${lead?.firstName} ${lead?.lastName}`;

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-blue-600" />
                        {dialogTitle}
                    </DialogTitle>
                    <DialogDescription>{dialogDescription}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Date Input */}
                    <div>
                        <Label htmlFor="callback-date">Date *</Label>
                        <Input
                            id="callback-date"
                            type="date"
                            value={formData.date}
                            onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                            min={moment().format("YYYY-MM-DD")}
                            className="mt-1"
                        />
                    </div>

                    {/* Time Input */}
                    <div>
                        <Label htmlFor="callback-time">Time *</Label>
                        <div className="flex items-center gap-2 mt-1">
                            <Clock className="h-4 w-4 text-gray-400" />
                            <Input
                                id="callback-time"
                                type="time"
                                value={formData.time}
                                onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                                className="flex-1"
                            />
                        </div>
                    </div>

                    {/* Time Info Display */}
                    {timeUntil && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-blue-600" />
                                <div>
                                    <p className="text-sm font-medium text-blue-900">
                                        Time from now: <span className="text-blue-700">{timeUntil}</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Note Field */}
                    <div>
                        <Label htmlFor="callback-note">Note (Optional)</Label>
                        <Textarea
                            id="callback-note"
                            value={formData.note}
                            onChange={(e) => setFormData(prev => ({ ...prev, note: e.target.value }))}
                            placeholder="Reason for callback, topics to discuss, etc..."
                            rows={3}
                            className="mt-1"
                        />
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} className="">
                        <Calendar className="h-4 w-4 mr-2" />
                        {mode === "reschedule" ? "Reschedule" : "Schedule"} Callback
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

